import { Amplify } from 'aws-amplify';
import {
  signUp,
  signIn,
  getCurrentUser,
  resetPassword,
  confirmResetPassword,
  signOut,
  AuthUser,
} from '@aws-amplify/auth';
import AuthConfig from '../libs/auth';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { accountFindOne } from '../libs/ohat-api';
import { Account } from '../models';
import i18n from '../i18n';

Amplify.configure({ Auth: AuthConfig });

interface UseAuth {
  isLoading: boolean;
  isAuthenticated: boolean;
  username: string;
  rawData?: AuthUser;
  account?: Account;
  isOwner: () => boolean;
  signUpCognito: (username: string, password: string) => Promise<Result>;
  signInCognito: (username: string, password: string) => Promise<string>;
  signOutCognito: () => void;
  resetPasswordCognito: (username: string) => void;
  fetchAccount: (sub: string) => void;
  confirmResetPasswordCognito: (
    email: string,
    confirmationCode: string,
    newPassword: string,
  ) => void;
}

interface Result {
  success: boolean;
  message: string;
}

const authContext = createContext({} as UseAuth);

export interface ChildComponentProps {
  children: React.ReactNode;
}

export const ProvideAuth: React.FC<ChildComponentProps> = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = () => {
  return useContext(authContext);
};

const useProvideAuth = (): UseAuth => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [rawData, setRawData] = useState<AuthUser>();
  const [account, setAccount] = useState<Account>();

  useEffect(() => {
    getCurrentUser()
      .then(async (result) => {
        setUsername(result.username);
        setRawData(result);
        await fetchAccount(result.username);
        setIsAuthenticated(true);
        setIsLoading(false);
      })
      .catch(async () => {
        // getCurrentUser() は成功したがアカウント取得等で失敗したケースでは
        // Cognito セッションが残らないようサインアウトする
        // (未ログインで reject した場合の signOut は no-op なので分岐不要)
        await forceSignOut();
        setUsername('');
        setIsAuthenticated(false);
        setIsLoading(false);
      });
  }, []);

  // signOut 自体の失敗は無視してセッション破棄を試みる
  const forceSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error(error);
    }
  };

  const signUpCognito = async (username: string, password: string) => {
    try {
      await signUp({ username, password });
      return { success: true, message: '' };
    } catch (error: any) {
      console.error(error);
      if (error.name === 'UsernameExistsException') {
        return {
          success: false,
          message: i18n.t('auth.emailExists'),
        };
      }
      return { success: false, message: i18n.t('auth.signUpFailed') };
    }
  };

  const resetPasswordCognito = async (email: string) => {
    try {
      await resetPassword({ username: email });
    } catch (error) {
      console.error(error);
    }
  };

  const confirmResetPasswordCognito = async (
    email: string,
    confirmationCode: string,
    newPassword: string,
  ) => {
    await confirmResetPassword({
      username: email,
      confirmationCode,
      newPassword,
    });
  };

  const signInCognito = async (email: string, password: string) => {
    try {
      try {
        await signIn({ username: email, password });
      } catch (error: any) {
        if (error.name !== 'UserAlreadyAuthenticatedException') {
          throw error;
        }
        // 前ユーザのセッションが残っている場合は破棄して一度だけ再試行する
        await forceSignOut();
        await signIn({ username: email, password });
      }
      const user = await getCurrentUser();
      setUsername(user.username);
      setIsAuthenticated(true);
      await fetchAccount(user.username);
      return user.username;
    } catch (error) {
      console.error(error);
      // アカウント取得に失敗した場合等にセッションを残さない
      await forceSignOut();
      setIsAuthenticated(false);
      setUsername('');
      return '';
    }
  };

  const fetchAccount = async (sub: string) => {
    const account = await accountFindOne(sub);
    if (account) setAccount(account);
  };

  const signOutCognito = async () => {
    try {
      await signOut();
      localStorage.clear();
      setUsername('');
      setIsAuthenticated(false);
      return { success: true, message: '' };
    } catch (error) {
      console.error(error);
      return {
        success: false,
        message: i18n.t('auth.signOutFailed'),
      };
    }
  };

  const isOwner = () => {
    return account?.role === 'owner';
  };

  return {
    isLoading,
    isAuthenticated,
    username,
    account,
    rawData,
    signUpCognito,
    signInCognito,
    signOutCognito,
    resetPasswordCognito,
    confirmResetPasswordCognito,
    fetchAccount,
    isOwner,
  };
};
